import React, { useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper,
	Collapse,
	ToggleButton,
	ToggleButtonGroup,
	IconButton,
	Tooltip,
} from "@mui/material";
import {
	Edit,
	ExpandMore,
	ExpandLess,
	Delete,
	Visibility,
	Close as CloseIcon,
	Refresh,
} from "@mui/icons-material";
import { StatusBadge, formatDate, daysBetween } from "../../utils/functions";

// ─── Work Orders Tab
const WorkOrdersTab = ({
	workOrders,
	totalCost,
	openCount,
	closedCount,
	woChartOpen,
	setWoChartOpen,
	woChartType,
	setWoChartType,
	onAddWO,
	onViewWO,
	onEditWO,
	onDeleteWO,
	onCloseWO,
	onReopenWO,
	classes,
}) => {
	const fmtMoney = (n) =>
		n != null ? `$${Number(n).toFixed(2).toLocaleString()}` : "—";

	// Cost by type for mini chart
	const costByType = useMemo(() => {
		const map = {};
		workOrders.forEach((wo) => {
			const type = wo.typeInfo?.Code || wo.type || "Other";
			map[type] = (map[type] || 0) + (wo.cost || 0);
		});
		return Object.entries(map)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 5);
	}, [workOrders]);

	const maxTypeCost = Math.max(...costByType.map(([, c]) => c), 1);

	return (
		<Box>
			{/* Cost Summary - Collapsible */}
			<Paper sx={{ mb: 2 }}>
				<Box
					onClick={() => setWoChartOpen((o) => !o)}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						p: 1.5,
						cursor: "pointer",
						"&:hover": { bgcolor: "action.hover" },
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="body2" fontWeight={700}>
							💰 Work Order Costs
						</Typography>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontFamily: "monospace" }}
						>
							{fmtMoney(totalCost)} total · {workOrders.length} WO
							{workOrders.length !== 1 ? "s" : ""} · {openCount}{" "}
							open · {closedCount} closed
						</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{woChartOpen && (
							<ToggleButtonGroup
								size="small"
								value={woChartType}
								exclusive
								onChange={(_, v) => v && setWoChartType(v)}
								onClick={(e) => e.stopPropagation()}
							>
								<ToggleButton
									value="bar"
									sx={{ px: 1, py: 0.5 }}
								>
									☲
								</ToggleButton>
								<ToggleButton
									value="breakdown"
									sx={{ px: 1, py: 0.5 }}
								>
									◔
								</ToggleButton>
							</ToggleButtonGroup>
						)}
						{woChartOpen ? <ExpandLess /> : <ExpandMore />}
					</Box>
				</Box>

				<Collapse in={woChartOpen}>
					<Box
						sx={{
							p: 2,
							pt: 0,
							display: "grid",
							gridTemplateColumns: "200px 1fr",
							gap: 2,
						}}
					>
						{/* Total Cost Card */}
						<Box className={classes.statCard}>
							<Typography
								className={classes.statNumber}
								color="primary"
							>
								{fmtMoney(totalCost)}
							</Typography>
							<Typography className={classes.statLabel}>
								Total Cost — All Time
							</Typography>
							<Box
								sx={{
									mt: 1,
									display: "flex",
									gap: 1,
									justifyContent: "center",
								}}
							>
								<Chip
									label={`${openCount} Open`}
									size="small"
									color="warning"
									variant="outlined"
								/>
								<Chip
									label={`${closedCount} Closed`}
									size="small"
									color="success"
									variant="outlined"
								/>
							</Box>
						</Box>

						{/* Cost by Type Breakdown */}
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								fontWeight={700}
								sx={{ mb: 1, display: "block" }}
							>
								COST BY TYPE
							</Typography>
							{costByType.length === 0 ? (
								<Typography
									variant="caption"
									color="text.disabled"
								>
									No cost data yet
								</Typography>
							) : (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 0.5,
									}}
								>
									{costByType.map(([type, cost]) => (
										<Box
											key={type}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<Typography
												variant="caption"
												sx={{
													width: 80,
													fontWeight: 600,
												}}
											>
												{type}
											</Typography>
											<Box
												sx={{
													flex: 1,
													height: 12,
													bgcolor: "action.hover",
													borderRadius: 1,
													overflow: "hidden",
												}}
											>
												<Box
													sx={{
														width: `${(cost / maxTypeCost) * 100}%`,
														height: "100%",
														bgcolor: "primary.main",
														borderRadius: 1,
													}}
												/>
											</Box>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													minWidth: 60,
													textAlign: "right",
												}}
											>
												{fmtMoney(cost)}
											</Typography>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Box>
				</Collapse>
			</Paper>

			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{openCount} open · {closedCount} closed
				</Typography>
				<Button variant="contained" onClick={onAddWO}>
					+ New Work Order
				</Button>
			</Box>

			{/* Table */}
			{workOrders.length === 0 ? (
				<Box className={classes.emptyState}>
					<Box className={classes.emptyIcon}>🔧</Box>
					<Typography>No work orders for this vehicle</Typography>
					<Button variant="outlined" onClick={onAddWO} sx={{ mt: 2 }}>
						Create First Work Order
					</Button>
				</Box>
			) : (
				<Box className={classes.tableWrap}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>WO #</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Vendor</TableCell>
								<TableCell>Opened</TableCell>
								<TableCell>Closed</TableCell>
								<TableCell>Downtime</TableCell>
								<TableCell>Cost</TableCell>
								<TableCell>Odometer</TableCell>
								<TableCell>Notes</TableCell>
								<TableCell align="center">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{workOrders.map((wo) => {
								const downtime = daysBetween(
									wo.openedDate,
									wo.closedDate,
								);
								const isOpen =
									(wo.statusInfo?.Code || wo.status) ===
									"Open";
								return (
									<TableRow
										key={wo.id}
										hover
										className={classes.clickableRow}
										onClick={() => onViewWO(wo)}
									>
										<TableCell>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													fontWeight: 600,
												}}
											>
												WO-
												{String(wo.id)
													.slice(-4)
													.padStart(4, "0")}
											</Typography>
										</TableCell>
										<TableCell>
											<StatusBadge
												status={
													wo.typeInfo?.Code || wo.type
												}
											/>
										</TableCell>
										<TableCell>
											<StatusBadge
												status={
													wo.statusInfo?.Code ||
													wo.status
												}
											/>
										</TableCell>
										<TableCell>
											{wo.vendor || "—"}
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{formatDate(wo.openedDate)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{wo.closedDate
													? formatDate(wo.closedDate)
													: "—"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{
													fontFamily: "monospace",
													color:
														downtime > 7
															? "error.main"
															: "text.secondary",
													fontWeight:
														downtime > 7
															? 700
															: 400,
												}}
											>
												{downtime}d
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{fmtMoney(wo.cost)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="caption"
												sx={{ fontFamily: "monospace" }}
											>
												{wo.odometerAtService
													? `${wo.odometerAtService.toLocaleString()} mi`
													: "—"}
											</Typography>
										</TableCell>
										<TableCell sx={{ maxWidth: 200 }}>
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
											>
												{wo.notes?.slice(0, 60)}
												{wo.notes?.length > 60
													? "…"
													: ""}
											</Typography>
										</TableCell>
										<TableCell
											align="center"
											onClick={(e) => e.stopPropagation()}
										>
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
													justifyContent: "center",
												}}
											>
												{isOpen ? (
													<Tooltip title="Close Work Order">
														<IconButton
															size="small"
															color="warning"
															onClick={() =>
																onCloseWO(wo)
															}
														>
															<CloseIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												) : (
													<Tooltip title="Reopen Work Order">
														<IconButton
															size="small"
															color="info"
															onClick={() =>
																onReopenWO(wo)
															}
														>
															<Refresh fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() =>
															onViewWO(wo)
														}
													>
														<Visibility fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Edit">
													<IconButton
														size="small"
														onClick={() =>
															onEditWO(wo)
														}
													>
														<Edit fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton
														size="small"
														color="error"
														onClick={() =>
															onDeleteWO(wo)
														}
													>
														<Delete fontSize="small" />
													</IconButton>
												</Tooltip>
											</Box>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</Box>
			)}
		</Box>
	);
};

export default WorkOrdersTab;
